import React, { useEffect, useRef, useState } from "react";
import { X, MapPin, BarChart3 } from "lucide-react";
import { Select, DateTimePicker } from "../form_components/Input";
import Button from "../ui/Button";
import { useRegions } from "../../context/RegionsContext";

const FilterPanel = ({
  filters,
  systems,
  isLoading,
  onApply,
  onReset,
  onClose,
  onFilterChange, 
}) => {
  const { selectedRegion } = useRegions();
  const [errors, setErrors] = useState({});
  const prevRegionIdRef = useRef(selectedRegion?.region_id);

  const aggregationLevels = [
    { value: "15mins", label: "15 Minutes" },
    { value: "hourly", label: "Hourly" },
    { value: "daily", label: "Daily" },
  ];

  // Clear siteId only on a real region change, not on initial mount
  // (otherwise the parent's default siteId gets wiped before allSites loads).
  useEffect(() => {
    const currentId = selectedRegion?.region_id;
    if (prevRegionIdRef.current !== currentId) {
      if (currentId) {
        onFilterChange?.("systemId", "");
        onFilterChange?.("streetId", "");
      }
      prevRegionIdRef.current = currentId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion?.region_id]);

  const setField = (key, value) => {
    onFilterChange?.(key, value);
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validateFilters = () => {
    const newErrors = {};
    if (!filters.systemId) newErrors.systemId = "Please select a system";
    if (!filters.startDate) newErrors.startDate = "Please select start date";
    if (!filters.startTime) newErrors.startTime = "Please select start time";
    if (!filters.endDate) newErrors.endDate = "Please select end date";
    if (!filters.endTime) newErrors.endTime = "Please select end time";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApplyKPI = () => {
    if (validateFilters()) onApply?.(filters);
  };

  const handleReset = () => {
    const reset = {
      systemId: "",
      streetId: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      aggregationLevel: "hourly",
    };
    setErrors({});
    onReset?.(reset);
  };

  return (
    <div className="h-full lg:flex-1 bg-white rounded-lg p-3 lg:min-h-0 border border-gray-200 overflow-y-auto">
      {/* Header (fixed) */}
      <div className="flex-none flex items-center justify-between px-3 py-2.5 border-b bg-white z-10">
        <h3 className="text-sm font-semibold text-gray-800">Filter</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          aria-label="Close filters"
          type="button"
        >
          <X size={16} />
        </button>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 min-h-0">
        <div className="px-3 py-3 space-y-3">
          <Select
            label="Select Intersection"
            value={
              filters.systemId != null &&
              filters.systemId !== "" &&
              filters.streetId != null &&
              filters.streetId !== ""
                ? `${filters.systemId},${filters.streetId}`
                : ""
            }
            onChange={(value) => {
              const [systemId, streetId] = value.split(",");
              setField("systemId", systemId);
              setField("streetId", streetId);
            }}
            options={systems}
            optionValue="composite_key"
            renderOption={(system) => `System ${system.system_id}, ${system.street_name}`}
            placeholder="Select a system"
            error={errors.systemId}
            required
            icon={MapPin}
            disabled={isLoading}
          />

          <div className="border-t pt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Start Date & Time
            </label>
            <DateTimePicker
              date={filters.startDate}
              time={filters.startTime}
              onDateChange={(value) => setField("startDate", value)}
              onTimeChange={(value) => setField("startTime", value)}
              error={errors.startDate || errors.startTime}
            />
          </div>

          <div className="border-t pt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              End Date & Time
            </label>
            <DateTimePicker
              date={filters.endDate}
              time={filters.endTime}
              onDateChange={(value) => setField("endDate", value)}
              onTimeChange={(value) => setField("endTime", value)}
              error={errors.endDate || errors.endTime}
            />
          </div>

          <Select
            label="Aggregation Level"
            value={filters.aggregationLevel}
            onChange={(value) => setField("aggregationLevel", value)}
            options={aggregationLevels}
            placeholder="Select aggregation level"
            error={errors.aggregationLevel}
            icon={BarChart3}
          />
        </div>
      </div>

      {/* Footer (fixed) */}
      <div className="flex-none border-t bg-gray-50 px-3 py-3">
        <div className="space-y-2">
          <Button
            onClick={handleApplyKPI}
            className="w-full"
            disabled={isLoading || !filters.systemId}
          >
            {isLoading ? "Loading..." : "Apply Filters"}
          </Button>

          <Button
            onClick={handleReset}
            variant="outline"
            className="w-full"
            disabled={isLoading}
          >
            Reset
          </Button>
        </div>

        <div className="mt-2 p-2 bg-blue-50 rounded-md border border-blue-200">
          <p className="text-[10px] leading-snug text-blue-800">
            <strong>Note:</strong> Some charts require both start and end dates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
